import type { Agent } from '@studio/models/agent';
import { isCausedBy } from '@studio/js-utils/logger';
import { isTransientDiscoveryFailure } from './commands/utils.js';

/**
 * Per-agent discovery command result. Accepted means mutate delivery only,
 * not a complete scan. Per rule: stay-in-scope — no new scheduler.
 */
export type DiscoveryCommandResult =
  | { readonly kind: 'accepted' }
  | { readonly kind: 'transient' }
  | { readonly kind: 'rejected'; readonly reason: 'missing-proxy' | 'other' };

/**
 * State of one Bridge's discovery flight.
 *
 * `pending` is a request this observer is joined to; `timeout` is the
 * presentation ceiling firing while that request is still owned — never a
 * second mutate, and never a settled result. Kept per Bridge so a healthy
 * sibling's success cannot erase another Bridge's degraded status.
 */
export type DiscoveryFlightState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'timeout' }
  | DiscoveryCommandResult;

const isFailing = (state: DiscoveryFlightState): boolean =>
  state.kind === 'transient' || state.kind === 'timeout' || state.kind === 'rejected';

/** Spec R9 selected-Bridge copy. */
export const R9_SELECTED_COPY =
  'Discovery could not reach the selected Bridge. We\u2019ll try again while this window is open. You can also choose Search Again.';

/** Spec R9 all-bridges copy. */
export const R9_SCAN_COPY =
  'Some discovery requests could not complete. We\u2019ll try again while this window is open. You can also choose Search Again.';

/** Spec R10 missing proxy. */
export const R10_NOT_READY_COPY =
  'The selected Bridge is not ready to search for cameras. Check its connection or choose another available Bridge.';

/** Spec R10 non-transient failure. */
export const R10_FAILED_COPY =
  'Discovery could not complete. Choose Search Again. If it continues, contact support.';

const NON_TRANSIENT_CODES = new Set([400, 401, 403, 404, 409, 422]);

const isNonTransientFailure = (cause: unknown): boolean => {
  // isCausedBy keeps message text only, so inspect structured status before transport fallbacks.
  let current = cause;
  const seen = new Set<unknown>();
  for (let depth = 0; depth < 8 && current && !seen.has(current); depth += 1) {
    seen.add(current);
    if (typeof current !== 'object') break;
    const node = current as { code?: unknown; cause?: unknown };
    if (typeof node.code === 'number' && NON_TRANSIENT_CODES.has(node.code)) return true;
    current = node.cause;
  }

  // Auth/permission/validation/decode outrank nested "failed to fetch" text.
  return (
    isCausedBy(cause, '401') ||
    isCausedBy(cause, '403') ||
    isCausedBy(cause, 'Unauthorized') ||
    isCausedBy(cause, 'Forbidden') ||
    isCausedBy(cause, 'permission') ||
    isCausedBy(cause, 'decode') ||
    isCausedBy(cause, 'validation')
  );
};

// Reuse transport classification, but let explicit auth/validation failures outrank
// transient-looking messages nested underneath them.
const isTransientFailure = (cause: unknown): boolean =>
  !isNonTransientFailure(cause) && isTransientDiscoveryFailure(cause);

/** Classify a discovery mutate rejection. Never invent a physical cause. */
export const classifyDiscoveryCause = (cause: unknown): DiscoveryCommandResult => {
  if (isCausedBy(cause, 'DV-681')) {
    return { kind: 'rejected', reason: 'missing-proxy' };
  }

  if (isTransientFailure(cause)) {
    return { kind: 'transient' };
  }

  return { kind: 'rejected', reason: 'other' };
};

/**
 * Pick inline R9/R10 copy from the current per-Bridge flight states.
 *
 * Recomputed from every record on every change, so a Bridge that recovers
 * clears only its own outcome. The selected Bridge is named only when it is
 * itself the sole failing Bridge — a failed sibling must not be presented as
 * the Bridge the user picked.
 */
export const aggregateDiscoveryFailureCopy = (
  states: ReadonlyMap<Agent['id'], DiscoveryFlightState>,
  selectedAgentId: Agent['id']
): string | undefined => {
  const failing = [...states].filter(([, state]) => isFailing(state));

  if (failing.length === 0) {
    return undefined;
  }

  const selected = states.get(selectedAgentId);
  if (selected?.kind === 'rejected' && selected.reason === 'missing-proxy') {
    return R10_NOT_READY_COPY;
  }

  if (failing.some(([, state]) => state.kind === 'rejected')) {
    return R10_FAILED_COPY;
  }

  const onlySelectedFailed = failing.length === 1 && failing[0]![0] === selectedAgentId;

  return onlySelectedFailed ? R9_SELECTED_COPY : R9_SCAN_COPY;
};
